// Work live events arrive as SSE. Browsers have EventSource; React Native
// (including macOS) does not, so token frames never reached the transcript
// and polling skipped painting assistant text. This helper uses EventSource
// on web and fetch+ReadableStream everywhere else.

import { Platform } from 'react-native';

export function consumeSseBuffer(buffer, onFrame) {
  const parts = String(buffer || '').split('\n\n');
  const rest = parts.pop() || '';
  for (const block of parts) {
    let event = 'message';
    const dataLines = [];
    for (const rawLine of block.split('\n')) {
      const line = rawLine.replace(/\r$/, '');
      if (!line || line.startsWith(':')) continue;
      if (line.startsWith('event:')) {
        event = line.slice(6).trim() || 'message';
        continue;
      }
      if (line.startsWith('data:')) {
        dataLines.push(line.slice(5).replace(/^ /, ''));
      }
    }
    if (!dataLines.length && event === 'message') continue;
    onFrame({ event, data: dataLines.join('\n') });
  }
  return rest;
}

function parseJsonData(data) {
  if (!data) return null;
  try {
    return JSON.parse(data);
  } catch {
    return null;
  }
}

/**
 * Subscribe to a Work SSE URL. `getUrl` may be async (fresh JWT).
 * Returns a close function. `onFatal` means the stream cannot continue
 * (native fetch failed, or EventSource entered CLOSED).
 *
 * `shouldSkipFrame(rawData)` runs before JSON.parse so a resume reconnect
 * that replays the persisted log does not pay to decode hundred-kilobyte
 * message_end frames the transcript has already ingested.
 */
export function openWorkEventStream({ getUrl, onEvent, onEot, onFatal, shouldSkipFrame }) {
  let closed = false;
  let source = null;
  let abort = null;
  let reader = null;

  const deliver = (data) => {
    if (shouldSkipFrame && shouldSkipFrame(data)) return;
    const parsed = parseJsonData(data);
    if (parsed) onEvent?.(parsed);
  };

  const close = () => {
    closed = true;
    try { source?.close(); } catch { /* already closed */ }
    try { abort?.abort(); } catch { /* already aborted */ }
    try { reader?.cancel(); } catch { /* already cancelled */ }
    source = null;
    abort = null;
    reader = null;
  };

  const start = async () => {
    let url;
    try {
      url = await getUrl();
    } catch {
      if (!closed) onFatal?.();
      return;
    }
    if (closed || !url) {
      if (!closed) onFatal?.();
      return;
    }

    if (Platform.OS === 'web' && typeof EventSource !== 'undefined') {
      source = new EventSource(url);
      source.onmessage = (msg) => {
        if (closed) return;
        deliver(msg?.data);
      };
      source.addEventListener('eot', () => {
        if (closed) return;
        onEot?.();
        // The server ends the response after eot; without closing here the
        // browser auto-reconnects with the same cursor and loops on eot.
        close();
      });
      source.onerror = () => {
        if (closed) {
          source?.close();
          return;
        }
        if (source && source.readyState === EventSource.CLOSED) {
          source.close();
          source = null;
          onFatal?.();
        }
      };
      return;
    }

    abort = typeof AbortController !== 'undefined' ? new AbortController() : null;
    try {
      const res = await fetch(url, {
        headers: { Accept: 'text/event-stream' },
        signal: abort?.signal,
      });
      if (closed) return;
      if (!res.ok || !res.body || typeof res.body.getReader !== 'function') {
        onFatal?.();
        return;
      }
      reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';
      while (!closed) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        buffer = consumeSseBuffer(buffer, (frame) => {
          if (closed) return;
          if (frame.event === 'eot') {
            onEot?.();
            close();
            return;
          }
          if (frame.event !== 'message') return;
          deliver(frame.data);
        });
      }
      if (!closed) onFatal?.();
    } catch (err) {
      if (closed || err?.name === 'AbortError') return;
      onFatal?.();
    }
  };

  start();
  return close;
}
